import React, { useState } from 'react';
import { CssBaseline, Drawer, List, ListItem, IconButton, Button, Tooltip } from '@mui/material';
import { Logout, Settings as SettingsIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import PaidIcon from '@mui/icons-material/Paid';
import HelpIcon from '@mui/icons-material/Help';
import HomeIcon from '@mui/icons-material/Home';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import BusinessIcon from '@mui/icons-material/Business';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import LogoBk from '../../assets/logo_bk.svg';
import { buttonStyles, DrawerHeader } from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { setUrl } from '../../features/stepper/stepperSlice';
import { resetCurrentUser } from 'src/features/user/userSlice';
import { UploadMailings } from 'src/pages/dashboard/components/UploadMailings';
import NoPaymentMethodAlert from 'src/pages/dashboard/components/NoPaymentMethodAlert';
import { PaymentMethodsStatus } from 'src/utils/enums/payment-method-status.enum';
import { logoutUser } from 'src/features/user/utils';

export const drawerWidth = 255;

const footerPaths = ['/home', '/settings', '/costs', '/help', '/history'];

interface SideMenuRrops {
  open?: boolean;
  handleToggle?: () => void;
  isBannerActive?: boolean;
}

const getButtonStyles = (open: boolean | undefined, isActive: boolean) => ({
  fontFamily: 'Instrument Sans',
  ...buttonStyles(open),
  ...(isActive && {
    backgroundColor: '#cee2ff',
    borderRadius: '0rem',
    boxShadow: 'none',
    color: '#000',
  }),
});

const BkDrawer: React.FC<SideMenuRrops> = ({ open, handleToggle, isBannerActive }) => {
  const activeUrl = useSelector((state: RootState) => state.stepper.activeUrl);

  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const location = useLocation();

  const onNavigate = (url: string) => {
    dispatch(setUrl(url));
    if (url === '/auth/sign-in') {
      dispatch(logoutUser());
      dispatch(resetCurrentUser());
    }
    navigate(url);
  };

  const currentUser = useSelector((state: RootState) => state.user.currentUser);

  const isCosMember = currentUser.teamRole === 'admin' || currentUser.teamRole === 'support';
  const paymentMethodsStatus = useSelector((state: RootState) => state.company.paymentMethodsStatus);

  const isCosAdmin = currentUser.teamRole === 'admin';
  const isOrganizationAdmin = currentUser.role === 'admin';

  const calculateDrawerPadding = (): string => {
    let padding = 0;
    if (isBannerActive) {
      padding += 40;
    }
    if (footerPaths.includes(location.pathname)) {
      padding += 40;
    }
    return `${padding / 16}rem`;
  };

  const mainListItems = isCosMember
    ? [
      {
        label: 'Job History',
        url: '/admin-history',
        icon: <WorkHistoryIcon />,
        additionalStyles: {
          ...buttonStyles(open),
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      },
      ...(isCosAdmin
        ? [
          {
            label: 'Pricing',
            url: '/admin-pricing',
            icon: <MonetizationOnIcon />,
            additionalStyles: {
              ...buttonStyles(open),
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          },
        ]
        : []),
      ...(isCosAdmin
        ? [
          {
            label: 'Company',
            url: '/admin-company',
            icon: <BusinessIcon />,
            additionalStyles: {
              ...buttonStyles(open),
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          },
        ]
        : []),
      ...(isCosAdmin
        ? [
          {
            label: 'Partner API',
            url: '/admin-partner',
            icon: <IntegrationInstructionsIcon />,
            additionalStyles: {
              ...buttonStyles(open),
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          },
        ]
        : []),
    ]
    : [
      {
        label: 'Home',
        url: '/home',
        icon: <HomeIcon />,
        additionalStyles: {
          ...buttonStyles(open),
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      },
      {
        label: 'Job History',
        url: '/history',
        icon: <WorkHistoryIcon />,
        additionalStyles: {
          ...buttonStyles(open),
          backgroundColor: `transparent`,
          boxShadow: 'none',
        },
      },
      // Hidden temporarily for BKAS-2437
      // {
      //   label: 'Price Estimator',
      //   url: '/costs',
      //   icon: <PaidIcon />,
      //   additionalStyles: {
      //     ...buttonStyles(open),
      //     backgroundColor: `transparent`,
      //     boxShadow: 'none',
      //   },
      // },
    ];

  const subListItems = isCosMember
    ? [{ label: 'Log Out', icon: <Logout />, url: '/auth/sign-in' }]
    : [
      { label: 'Settings', icon: <SettingsIcon />, url: '/settings' },
      {
        label: 'Help',
        icon: <HelpIcon />,
        url: 'https://info.certificateofservice.com/help',
        isExternal: true,
      },
    ];

  return (
    <React.Fragment>
      <CssBaseline />
      <Drawer
        variant="permanent"
        sx={{
          maxHeight: '95vh',
          width: open ? drawerWidth : (theme) => theme.spacing(12),
          flexShrink: 0,
          whiteSpace: 'nowrap',
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          '& .MuiDrawer-paper': {
            maxHeight: '100vh',
            backgroundColor: (theme) => theme.palette.primary.light,
            display: 'flex',
            marginTop: isBannerActive ? '3.25rem' : '',
            border: 'none',
            justifyContent: 'space-between',
            width: open ? drawerWidth : (theme) => theme.spacing(12),
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            overflowX: 'hidden',
            // Sits above the page content so that horizontally scrolled content
            // passes behind the drawer instead of on top of it. Kept below the
            // footer (20) and the banner (100) so neither gets covered.
            zIndex: 10,
            paddingBottom: calculateDrawerPadding(),
          },
        }}
        open={open}
      >
        <div>
          <DrawerHeader
            sx={{
              display: 'flex',
              justifyContent: open ? 'space-between' : 'center',
              paddingLeft: '1rem',
            }}
          >
            {open && <img src={LogoBk} style={{ height: '2.1875rem' }} />}
            <IconButton onClick={handleToggle}>
              <MenuOpenIcon
                sx={{
                  color: '#000',
                }}
              />
            </IconButton>
          </DrawerHeader>
          <List sx={{ paddingTop: '0rem' }}>
            {!isCosMember && (
              <>
                <ListItem
                  onClick={() => { }}
                  sx={{
                    paddingBottom: paymentMethodsStatus === PaymentMethodsStatus.MISSING ? '0.3125rem' : '1.25rem',
                    '& .MuiButtonBase-root:hover': {
                      boxShadow: 'none',
                    },
                  }}
                >
                  <UploadMailings
                    sx={{
                      ...buttonStyles(open),
                      justifyContent: 'center',
                      color: '#FFF',
                      padding: '0.625rem',
                    }}
                    isOpen={open}
                  />
                </ListItem>
                {open && paymentMethodsStatus === PaymentMethodsStatus.MISSING && (
                  <ListItem
                    onClick={() => { }}
                    sx={{
                      paddingBottom: '1.75rem',
                      '& .MuiButtonBase-root:hover': {
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <NoPaymentMethodAlert isDrawer isOrganizationAdmin={isOrganizationAdmin} />
                  </ListItem>
                )}
              </>
            )}
            {mainListItems.map((item) => {
              return (
                <Tooltip
                  disableHoverListener={open}
                  disableFocusListener={open}
                  disableTouchListener={open}
                  placement={'top'}
                  title={item.label}
                  key={item.label}
                >
                  <ListItem
                    onClick={() => onNavigate(item.url)}
                    sx={{
                      paddingTop: '0rem',
                      paddingBottom: '0.3125rem',
                      '& .MuiButtonBase-root:hover': {
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <Button
                      startIcon={item.icon}
                      variant="contained"
                      sx={{
                        fontFamily: 'Instrument Sans',
                        ...item.additionalStyles,
                        ...(activeUrl === item.url
                          ? {
                            backgroundColor: '#cee2ff',
                            borderRadius: '0rem',
                            boxShadow: 'none',
                            color: '#000',
                          }
                          : undefined),
                      }}
                    >
                      {open && item.label}
                    </Button>
                  </ListItem>
                </Tooltip>
              );
            })}
          </List>
        </div>
        <List>
          {subListItems.map((item, index) => {
            const isActive = activeUrl === item.url;
            const commonProps = {
              startIcon: item.icon,
              sx: getButtonStyles(open, isActive),
            };

            return (
              <Tooltip
                disableHoverListener={open}
                disableFocusListener={open}
                disableTouchListener={open}
                placement={'top'}
                key={item.label}
                title={item.label}
              >
                <ListItem
                  sx={{
                    paddingTop: '0rem',
                    paddingBottom: '0.3125rem',
                    '& .MuiButtonBase-root:hover': {
                      boxShadow: 'none',
                    },
                  }}
                  onClick={() => (item.isExternal ? undefined : onNavigate(item.url))}
                >
                  {item.isExternal ? (
                    <Button {...commonProps} href={item.url} target="_blank" rel="noopener noreferrer">
                      {open && item.label}
                    </Button>
                  ) : (
                    <Button {...commonProps}>{open && item.label}</Button>
                  )}
                </ListItem>
              </Tooltip>
            );
          })}
        </List>
      </Drawer>
    </React.Fragment>
  );
};

export default BkDrawer;
